import { PaneSlot } from "../rail/Rail.styles";
import { OpenPane } from "./definePane";

/**
 * Two frames with identities of their own, each showing its pane in whichever slot it has been given. A
 * frame keeps its identity as it moves between the rail and the stage, which is what lets its pane keep its
 * element, its state and its slide: the frame changes slot, a pane never changes frame.
 */
export type FrameId = "first" | "second";

export const frameIds: Array<FrameId> = ["first", "second"];

export interface Frame {
    pane: OpenPane | undefined;
    /** Kept while the frame is empty, so its pane parks at, and slides out past, the edge it last showed in. */
    slot: PaneSlot;
}

/**
 * Which pane sits where. Every transition is a pure function of this so the rules can be tested without
 * React. The rules: a stage pane never takes the rail from a right pane; a right pane arriving sends a stage
 * pane in the rail home to the stage, over whatever the stage was showing; a stage pane arriving replaces
 * what the stage shows and leaves the rail alone.
 */
export interface Placement {
    first: Frame;
    second: Frame;
}

export const emptyPlacement: Placement = {
    first: { pane: undefined, slot: "stage" },
    second: { pane: undefined, slot: "right" },
};

function isOpen(frame: Frame): boolean {
    return frame.pane !== undefined;
}

/** Whether the frame shows a pane that belongs on the stage, whatever slot it is in right now. */
function holdsStagePane(frame: Frame): boolean {
    return frame.pane?.definition.slot === "stage";
}

/** The frame showing in a slot, if one is. Two open frames are never in the same slot. */
export function getFrameIdInSlot(placement: Placement, slot: PaneSlot): FrameId | undefined {
    return frameIds.find((id) => placement[id].slot === slot && isOpen(placement[id]));
}

function getFreeFrameId(placement: Placement): FrameId | undefined {
    return frameIds.find((id) => !isOpen(placement[id]));
}

export function getPaneInSlot(placement: Placement, slot: PaneSlot): OpenPane | undefined {
    const id = getFrameIdInSlot(placement, slot);
    return id === undefined ? undefined : placement[id].pane;
}

export function hasPaneOnRight(placement: Placement): boolean {
    return getFrameIdInSlot(placement, "right") !== undefined;
}

export function hasPaneOnStage(placement: Placement): boolean {
    return getFrameIdInSlot(placement, "stage") !== undefined;
}

function withFrame(placement: Placement, id: FrameId, frame: Frame): Placement {
    return { ...placement, [id]: frame };
}

function moveFrame(placement: Placement, id: FrameId, slot: PaneSlot): Placement {
    return withFrame(placement, id, { ...placement[id], slot });
}

/** Shows the pane in the slot: in the frame already showing there, or in a free one moved there. */
function showInSlot(placement: Placement, slot: PaneSlot, pane: OpenPane): Placement {
    // When no frame shows in the slot at most one is open, so a free one always exists.
    const id = getFrameIdInSlot(placement, slot) ?? getFreeFrameId(placement)!;
    return withFrame(placement, id, { pane, slot });
}

/**
 * Sends a stage pane that is showing in the rail home to the stage, if the stage is free to take it. Left
 * alone otherwise: a right pane stays where it is, and a stage pane with nowhere to go is for the caller to
 * replace or close.
 */
function sendStagePaneHome(placement: Placement): Placement {
    const railId = getFrameIdInSlot(placement, "right");
    if (railId === undefined || !holdsStagePane(placement[railId]) || hasPaneOnStage(placement)) {
        return placement;
    }
    return moveFrame(placement, railId, "stage");
}

/**
 * The same for a right pane arriving, which the stage pane in the rail has to make way for: it takes the
 * stage over whatever was there rather than being thrown away. The case this exists for is a review opened
 * from a document parked in the rail, where the review is about that document, so the document is the one
 * thing that has to stay on show; a pane on the stage from before is the older context and gives way.
 */
function sendStagePaneHomeOverStage(placement: Placement): Placement {
    const railId = getFrameIdInSlot(placement, "right");
    if (railId === undefined || !holdsStagePane(placement[railId])) {
        return placement;
    }
    return moveFrame(closeSlot(placement, "stage"), railId, "stage");
}

/** Shows the pane in its own slot. Something arriving in the rail first sends a stage pane there home. */
export function openPane(placement: Placement, pane: OpenPane): Placement {
    const cleared = pane.definition.slot === "right" ? sendStagePaneHomeOverStage(placement) : placement;
    return showInSlot(cleared, pane.definition.slot, pane);
}

/** Empties one frame and leaves the other exactly as it was. Returns the same object when there was nothing to close. */
export function closeFrame(placement: Placement, id: FrameId): Placement {
    return isOpen(placement[id]) ? withFrame(placement, id, { ...placement[id], pane: undefined }) : placement;
}

export function closeSlot(placement: Placement, slot: PaneSlot): Placement {
    const id = getFrameIdInSlot(placement, slot);
    return id === undefined ? placement : closeFrame(placement, id);
}

/** Moves the stage pane into the rail. Only possible while the rail is free, since the rail holds one thing. */
export function collapseStage(placement: Placement): Placement {
    const stageId = getFrameIdInSlot(placement, "stage");
    if (stageId === undefined || hasPaneOnRight(placement)) {
        return placement;
    }
    return moveFrame(placement, stageId, "right");
}

/** Moves a stage pane showing in the rail back onto the stage. Only possible while the stage is free. */
export function expandStage(placement: Placement): Placement {
    return sendStagePaneHome(placement);
}

/**
 * Opens a stage pane while keeping the pane the stage was showing: it moves into the rail when the rail is
 * free, and is replaced as by any stage open when it is not. Collapse followed by open, which is exactly
 * what a user would do by hand to see both.
 */
export function openKeepingStagePane(placement: Placement, pane: OpenPane): Placement {
    return openPane(collapseStage(placement), pane);
}

/**
 * Frees the rail for something that is not a pane, such as the chat: a stage pane showing there goes home
 * when the stage is free and closes when it is not, and a right pane closes. Returns the same object when the
 * rail was already free.
 */
export function vacateRight(placement: Placement): Placement {
    return closeSlot(sendStagePaneHome(placement), "right");
}

function openPanes(placement: Placement): Array<OpenPane> {
    return frameIds.map((frame) => placement[frame].pane).filter((pane): pane is OpenPane => pane !== undefined);
}

/**
 * The frames a transition empties or replaces, which are the ones with something to lose. Read off the
 * placements rather than off the kind of pane arriving: a frame that only changes slot keeps its pane.
 */
export function framesLosingContent(previous: Placement, next: Placement): Array<FrameId> {
    return frameIds.filter((frame) => previous[frame].pane !== undefined && next[frame].pane !== previous[frame].pane);
}

/** The panes a transition closes or replaces: in no frame any more, whichever frame they were in before. */
export function leavingPanes(previous: Placement, next: Placement): Array<OpenPane> {
    const remaining = new Set(openPanes(next).map((pane) => pane.instanceId));
    return openPanes(previous).filter((pane) => !remaining.has(pane.instanceId));
}

/**
 * Closes the panes that a pane leaving had opened as its own, so a document a form showed beside itself goes
 * with the form instead of lingering on the stage. Read off ownership rather than off the slot: the stage may
 * show a pane the owner never opened, such as one another pane put there in the same breath as it replaced
 * the owner, and that one stays. An owned pane that is already gone has nothing left to close.
 */
export function closePanesOwnedByLeaving(previous: Placement, next: Placement): Placement {
    const leavingInstanceIds = new Set(leavingPanes(previous, next).map((pane) => pane.instanceId));
    return frameIds.reduce((placement, id) => {
        const ownerInstanceId = placement[id].pane?.ownerInstanceId;
        return ownerInstanceId !== undefined && leavingInstanceIds.has(ownerInstanceId) ? closeFrame(placement, id) : placement;
    }, next);
}

export type ChatChange = "close" | "unexpand";

/**
 * What the chat has to give up for a pane arriving. The rail holds one occupant, so a pane taking it closes
 * the chat; a pane arriving on the stage only folds an expanded chat back into the rail, since the overlay
 * would otherwise cover the stage. A stage pane opened beside the one on the stage takes the rail too when it
 * moves that one there.
 */
export function chatChangesFor(arrivingSlot: PaneSlot, previous: Placement, next: Placement): Array<ChatChange> {
    if (arrivingSlot === "right") {
        return ["close"];
    }
    const takesRail = !hasPaneOnRight(previous) && hasPaneOnRight(next);
    return takesRail ? ["unexpand", "close"] : ["unexpand"];
}

export interface FoldControlInput {
    isStagePane: boolean;
    slot: PaneSlot;
    hasPaneOnRight: boolean;
    hasPaneOnStage: boolean;
    isChatOpen: boolean;
}

/**
 * Which fold button a pane's header offers, if any. Only stage panes fold, and each slot holds one thing, so
 * collapsing is offered only while the rail is empty, the chat included, and expanding only while the stage
 * is: a stage pane in the rail beside a document waits until the document has gone.
 */
export function foldControlFor(input: FoldControlInput): "collapse" | "expand" | undefined {
    if (!input.isStagePane) {
        return undefined;
    }
    if (input.slot === "right") {
        return input.hasPaneOnStage ? undefined : "expand";
    }
    return input.hasPaneOnRight || input.isChatOpen ? undefined : "collapse";
}

/**
 * What a pane on the stage does when a link in it is followed. The panes outlive a navigation on purpose, but
 * a stage pane covers the page, so it makes way: into the rail while the rail is free, so the pane stays
 * beside the page, and closed when the rail is taken by a pane or the chat.
 */
export function stageResponseToFollowedLink(hasPaneOnStage: boolean, isRailOccupied: boolean): "close" | "collapse" | undefined {
    if (!hasPaneOnStage) {
        return undefined;
    }
    return isRailOccupied ? "close" : "collapse";
}
